/** @jsx isolateComponent */
import Downshift, { StateChangeOptions, GetRootPropsOptions } from 'downshift';
import React, {
  ComponentType,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { get } from 'lodash';
import { Border, Colors } from '@zapier/theme';
import Icon from '@zapier/common-components/src/components/Icon';
import TextInput from '@zapier/common-components/src/components/TextInput';
import { Menu as SelectFieldMenu } from '@zapier/common-components/src/components/SelectField';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';
import useDebounce from './useDebounce';

type Item = any;
type AsyncSearchProps = {
  /**
   * Accessibility label that describes this component. This should be supplied
   * if no corresponding `label` element is used.
   */
  ariaLabel?: string;
  /**
   * Autofocus text input on render
   */
  autoFocus?: boolean;
  /** Message that is shown when no results are found */
  emptyMessage: string;
  /**
   * Callback that will be used to generate the react `key` to be used for each item on the menu list.
   * Defaults to a `value` property on each item object.
   */
  getKeyForItem?: (item: Item) => string;
  /**
   * Callback that will be used to generate the string label to be rendered for each item on the menu list.
   * Defaults to a `label` property on each item object.
   */
  getLabelForItem: (item: Item) => string;
  /**
   * HTML `id` attribute for the `input` element. This prop is named `inputId`
   * instead of `id` for consistency with the Downshift API.
   */
  inputId?: string;
  /**
   * Disables the input and makes it non-interactive.
   */
  isDisabled?: boolean;
  /**
   * Callback that will be used to determine if an item is disabled.
   * Defaults to the value of `item.isDisabled`.
   */
  isItemDisabled: (item: Item) => boolean;
  /**
   * `id` of the label that connects to this select field. This prop is
   * required because Downshift demands it. If we don't provide this prop to
   * Downshift, it will render a bogus, auto-generated aria-labelledby value.
   */
  labelId?: string;
  /** a11y label that describes the options available inside the select menu, e.g. "List of items", "List of Countries", etc. */
  menuAriaLabel: string;
  /**
   * The Component to use for rendering each item in the list of `items`. Defaults to an internal implementation.
   */
  MenuItemComponent?: ComponentType<any>;
  /**
   * Callback that gets called when the user selects an item and the selected item has changed.
   */
  onChange: (item: Item) => void;
  /**
   * Callback that gets called as the user changes the value while typing. It
   * is passed the current value of the `Typeahead` input field.
   */
  onInputValueChange: (inputValue: string) => Promise<Array<Item>>;
  /**
   * Placeholder to render inside the select toggle when no item is selected.
   */
  placeholder: string;
};
const Styles = labelStyles(__filename, {
  root: (props: AsyncSearchProps) => [
    {
      position: 'relative',
    },
    props.isDisabled &&
      withModifier('disabled', {
        opacity: 0.7,
      }),
  ],
  icon: {
    display: 'flex',
    justifyContent: 'center',
    marginLeft: 12,
    zIndex: 2,
  },
  input: {
    alignItems: 'center',
    border: `${Border.size.default} solid ${Border.color.default}`,
    borderRadius: Border.radius.default,
    display: 'grid',
    gridTemplateColumns: '20px 1fr',
    '&:focus-within': {
      borderColor: Colors.heroine,
    },
  },
});

const AsyncSearch: React.FC<AsyncSearchProps> = props => {
  // Holds the value of the search text input
  const [inputValue, setInputValue] = useState('');

  // This item cache will be used to show immediate results but the API
  // will still be made even when a matching cache item is found and results
  // will update when the API returns
  const [itemCache, setItemCache] = useState({});

  // This keeps the loading state by search term since the user can keep changing
  // the input text when we are loading another result
  const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({});

  // Holds a ref to the search text input
  const inputRef = useRef<HTMLInputElement>();

  // Holds the debounced value so search isn't called on every keystroke
  const debouncedValue = useDebounce(inputValue, 300);
  const { getLabelForItem, onInputValueChange } = props;
  const itemToString = useCallback(
    item => (item ? getLabelForItem(item) : ''),
    [getLabelForItem]
  );

  const handleInputValueChanged = (value: string) => setInputValue(value);

  useEffect(() => {
    if (debouncedValue) {
      // set loading for this value
      setIsLoading(loading => ({ ...loading, [debouncedValue]: true }));

      // load the results for this value
      onInputValueChange(debouncedValue).then(results => {
        // save the result for this value
        setItemCache(cache => ({ ...cache, [debouncedValue]: results }));

        // remove the loading indicator for the value
        setIsLoading(loading => ({ ...loading, [debouncedValue]: false }));
      });
    } else {
      // Use an empty array for blank search
      setItemCache(cache => ({ ...cache, [debouncedValue]: [] }));
    }
  }, [debouncedValue, onInputValueChange]);

  const onStateChange = (changes: StateChangeOptions<Item>) => {
    if (changes.hasOwnProperty('selectedItem')) {
      // If there is a selected item just ignore it and clear
      // the input since we are only searching
      setInputValue('');
    } else if (changes.hasOwnProperty('inputValue')) {
      // We do this here so that we don't catch the selection event
      setInputValue(changes.inputValue || '');

      handleInputValueChanged(changes.inputValue || '');
    }
  };

  return (
    <Downshift
      inputId={props.inputId}
      itemToString={itemToString}
      labelId={props.labelId}
      onChange={props.onChange}
      onStateChange={onStateChange}
      selectedItem={null}
    >
      {({
        clearSelection,
        getInputProps,
        getItemProps,
        getMenuProps,
        getRootProps,
        highlightedIndex,
        isOpen,
        toggleMenu,
      }) => {
        // Downshift always renders an auto-generated aria-labelledby value
        // which refers to no actual element on the page. If we want to
        // omit a <label> element and instead label this component directly
        // via aria-label, Downshift will continue to render the bogus
        // aria-labelledby. The solution is therefore to force a null
        // aria-labelledby if no label is passed.
        const ariaLabelledBy = props.labelId || null;
        const items = get(itemCache, inputValue, []);
        const showEmpty =
          inputValue &&
          isOpen &&
          isLoading[inputValue] === false &&
          items.length === 0;
        const showValues = !showEmpty && isOpen;
        return (
          <div
            {...getRootProps(
              ({
                'aria-labelledby': ariaLabelledBy,
              } as unknown) as GetRootPropsOptions,
              {
                suppressRefError: true,
              }
            )}
            aria-label={props.ariaLabel}
            css={Styles.root(props)}
          >
            {/* eslint-disable-next-line */}
            <div
              css={Styles.input}
              onClick={() => {
                // Focus the input when any part of this div is clicked
                if (inputRef && inputRef.current) {
                  inputRef.current.focus();
                }
              }}
            >
              <div css={Styles.icon}>
                <Icon color="batmobile" name="search" />
              </div>
              <TextInput
                {...getInputProps()}
                ariaLabel={props.ariaLabel}
                ariaLabelledBy={ariaLabelledBy}
                autoCapitalize="off"
                autoComplete="new-password"
                autoCorrect="off"
                autoFocus={props.autoFocus}
                clearSelection={clearSelection}
                getLabelForItem={props.getLabelForItem}
                inputRef={inputRef}
                isBorderHidden={true}
                isDisabled={props.isDisabled}
                isOpen={isOpen}
                labelId={props.labelId}
                placeholder={props.placeholder}
                spellCheck="false"
                toggleMenu={toggleMenu}
                value={inputValue}
              />
            </div>
            {showValues && (
              <SelectFieldMenu
                ariaLabel={props.menuAriaLabel}
                getItemProps={getItemProps}
                getKeyForItem={props.getKeyForItem}
                getLabelForItem={props.getLabelForItem}
                getMenuProps={getMenuProps}
                highlightedIndex={highlightedIndex}
                isItemDisabled={props.isItemDisabled}
                isLoading={get(isLoading, inputValue, false)}
                isOpen={isOpen}
                items={items}
                MenuItemComponent={props.MenuItemComponent}
              />
            )}
            {showEmpty && (
              <SelectFieldMenu
                ariaLabel={props.menuAriaLabel}
                getItemProps={getItemProps}
                getKeyForItem={(item: string) => item}
                getLabelForItem={(item: string) => item}
                getMenuProps={getMenuProps}
                isItemDisabled={() => true}
                isOpen={isOpen}
                items={[props.emptyMessage]}
              />
            )}
          </div>
        );
      }}
    </Downshift>
  );
};

AsyncSearch.defaultProps = {
  emptyMessage: 'No results found',
  getKeyForItem: item => item.id,
  getLabelForItem: item => item.label,
  isDisabled: false,
  isItemDisabled: (item: Item) => item.isDisabled,
};
export default AsyncSearch;
